import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 9 — "What Raisy remembers about me" (D-206-010 row 9).
 *
 * **Who does what:** the person looks up what the machine kept about the work it did for him; Raisy
 * shows him the size of that record and sends him to the place he can read it.
 * **What he sees:** that every call is on the record, HOW MANY there are for this company, and where
 * to read them — plus, stated plainly, the two things that are not built.
 * **What he feels:** *"nothing hidden — I can see what it did and why"* (FTUE W2) and *"my data, my
 * machines"* (Sam W6). These are the first questions a distrusting user asks, which is why
 * D-206-011 refused the option that left the page silent about them.
 *
 * ⚠️ THE COUNT IS REAL AND RECONCILES — IT IS NOT A DECORATIVE NUMBER. It comes from
 * `POST /bot/call-history`, the audit trail a person is allowed to read (the memory-facts route is
 * service-only), filtered to this company; its `pagination.total` IS the number of rows in that
 * trail for the filter, so the figure on screen and the rows on the activity surface cannot disagree.
 * A count nobody can reconcile would be worse than no count: it would look like a fact.
 *
 * ⚠️ WHAT IT DOES NOT CLAIM, AND WHY THAT IS THE HONEST MINIMUM RATHER THAN AN OMISSION:
 *   · **the remembered FACTS are not shown here.** Their route is a service surface
 *     (`_require_service`), so a person-facing view of them does not exist yet.
 *   · **there is NO forget path.** No delete route exists for a remembered fact — verified by
 *     enumerating the memory routes, not assumed. Promising a forget that does not propagate would
 *     fail the one proof that matters: that the NEXT turn does not recall it.
 * Both absences are STATED on the row (D-206-011 made that a requirement, not a courtesy), next to
 * the count that IS real, so the person can tell which half he is looking at.
 */
export const MemorySection = ({ settings }: { settings: UserSettings }) => {
  const { receipts } = settings;

  const value =
    receipts.state === "loading" ? (
      <span className="text-muted-foreground">Counting the record…</span>
    ) : receipts.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {receipts.reason}. Nothing has been lost.
      </span>
    ) : (
      <span className="flex flex-col gap-1 font-normal">
        <span>
          {receipts.value === 0
            ? "Nothing has been done for this company yet"
            : `${receipts.value.toLocaleString()} ${receipts.value === 1 ? "call is" : "calls are"} on the record for this company`}
          <span className="ml-2 text-muted-foreground">
            — what each one was given, what it answered, and when
          </span>
        </span>
        <span className="text-xs text-muted-foreground">
          Not built yet: a view of the facts Raisy has remembered about you on their own, and a way to
          forget one. Until they exist you are told that here rather than shown an empty box.
        </span>
      </span>
    );

  return (
    <SettingsSection
      title="What Raisy remembers about me"
      hint="What the machine kept about the work it did for you, and where you can read it."
      value={value}
      link={{ to: "/activity-feed", label: "Open the record of what was done" }}
    />
  );
};
